import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  PaginationItem,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridFilteredDescendantCountLookupSelector,
  useGridApiRef,
  useGridSelector,
  gridFilteredSortedRowIdsSelector,
  GridCellEditStopReasons,
  GridRowId,
  GridRowTreeNodeConfig,
  GridPagination,
} from '@mui/x-data-grid-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrayParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from 'use-query-params';

import { getCookie } from 'common/utils/cookies';

import {
  getGantCsv,
  getArtFilters,
  createNewSavedQuery,
  getSavedQuery,
  getProjectsFilters,
  editQuery,
  saveQuery,
  deleteSavedQuery,
} from '../../../../packages/common/api';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import CheckedRadioIcon from '../../../../packages/common/shared-ui/src/icons/CheckedRadioIcon';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DownloadButton from '../../../../packages/common/shared-ui/src/icons/DownloadButton';
import FilterIcon from '../../../../packages/common/shared-ui/src/icons/FilterIcon';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import UncheckedRadioIcon from '../../../../packages/common/shared-ui/src/icons/UncheckedRadioIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';
import { getTranslatedText } from '../../../../packages/common/utils/getTranslatedText';
import { filtersToQuery } from '../../../../packages/common/utils/gridFiltersHelper';
import {
  filterDateValues,
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  formattedParamsData,
  paramsToQuery,
  queryToParams,
} from '../../../../packages/common/utils/gridFiltersHelperNew';
import { LanguageMap } from '../../../../packages/common/utils/languageMap';
import CommonPage from '../../CommonPage/CommonPage';
import GridFiltersModalNew from '../../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanel from '../../GridFiltersPanel/GridFiltersPanel';
import GridFiltersPanelNew from '../../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../../GridSaveQueryModal/GridSaveQueryModal';
import { store } from '../../app/redux';
import { useWebSockets } from '../../resourcePlan/useWebsockets';
import ArtEditCellSelect from '../components/ArtEditCellSelect/ArtEditCellSelect';

function sleep(delay = 0) {
  return new Promise(resolve => {
    setTimeout(resolve, delay);
  });
}

// Original class: datePicker
const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: 0,
      color: '#7174AC',
      ...(changedStyle && {
        color: '#212346',
      }),
      fontSize: '12px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      ...(changedStyle && {
        color: '#212346',
      }),
      padding: 0,
      '&::placeholder': {
        color: '#7174AC',
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const ArtContainer = () => {
  const getLocalString = useTranslation();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingPageChange, setPendingPageChange] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const expansionState = React.useRef({});
  const [pagination, setPagination] = useState({ count: 0, page: 0 });
  const [rowsToUpdate, setRowsToUpdate] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [firstLoadGroup, setFirstLoadGroup] = useState(true);
  const [open, setOpen] = useState(false);
  const [paramsLoading, setParamsLoading] = useState(true);
  const [openGantIssueModal, setOpenGantIssueModal] = useState(false);
  const [openGantIssueRelatedModal, setOpenGantIssueRelatedModal] =
    useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [queryData, setQueryData] = useState(null);
  const pageUseParams = useParams();
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [query, setQuery] = useQueryParams({
    filters: StringParam,
    project: StringParam,
    totals: StringParam,
    sorts: StringParam,
    fields: StringParam,
    limit: 100,
    offset: NumberParam,
    page: NumberParam,
  });
  const formatDate = useCallback(dateStr => {
    if (dateStr) {
      return dayjs(dateStr).format('DD.MM.YYYY');
    }
    return dateStr;
  }, []);
  const apiRef = useGridApiRef();
  const dataGridProRef = useRef(null);
  const accessToken = getCookie('access_token');
  const pageTitle = getLocalString('ArtContainer', 'title');
  const [showSavingError, setShowSavingError] = useState({
    state: false,
    errors: [],
  });
  const [artErrorValue, setArtErrorValue] = useState('save_all');
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [artRowsErrors, setArtRowsErrors] = useState([]);
  const [ganttStartDateErrors, setGanttStartDateErrors] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [tempCellValue, setTempCellValue] = useState([]);
  const socket = useWebSockets();
  const state = store.getState();
  const [artTableData, setArtTableData] = useState([]);
  const [artTableRowsData, setArtTableRowsData] = useState([]);
  const [artTableColumnsData, setArtTableColumnsData] = useState([]);
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [queryModalState, setQueryModalState] = useState(null);
  const [saveLoader, setSaveLoader] = useState(false);
  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  const isCellEditable = params => {
    return !!params.row.columnsEditable?.[params.field]?.isEditable;
  };
  const handleSort = useCallback(
    sortModel => {
      const newSorts = sortModel
        .map(item => `${item.field};${item.sort}`)
        .join(',');
      setQuery(prevQuery => ({ ...prevQuery, sorts: newSorts }));
      setIsLoading(true);
    },
    [setQuery],
  );
  const sortModel = useMemo(() => {
    if (!query.sorts) {
      return [];
    }
    return query.sorts.split(',').map(sort => {
      const [field, sortDirection] = sort.split(';');
      return { field, sort: sortDirection };
    });
  }, [query.sorts]);
  const handleGetRedirectUrl = useCallback(
    ({ id, columnType }) => {
      const issueLink = query.project
        ? `/projects/${query.project}/issues/${id}`
        : `/issues/${id}`;
      switch (columnType) {
        case 'project':
          return `${window.location.origin}/projects/${id}`;
        case 'issue':
        case 'issue.subject':
        case 'id':
          return issueLink;
        default:
          return '';
      }
    },
    [query.project],
  );
  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
      setIsLoading(true);
    },
    [setQuery],
  );
  const navigate = useNavigate();
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );
  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'art');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          window.open(`/finances/query/${res.id}`, '_self');
        }
      } catch (error) {
        handleError(error.response);
      }
      setOpenSaveQueryModal(false);
    },
    [currentParams, queryModalState, pageUseParams.queryId, handleError],
  );

  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    async function getParamsData() {
      const response = await getArtFilters(params);
      if (!ignore) {
        return response;
      }
    }

    getParamsData()
      .then(res => {
        const { data: paramsData } = res;
        const params = formatFilters(paramsData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleError, pageUseParams.queryId]);
  useEffect(() => {
    setQueryData(null);
    let ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries()
        .then(res => {
          res && setQueryData(res);
        })
        .catch(error => handleError(error.response));
    }
  }, [handleError, pageUseParams.queryId]);
  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);
  useEffect(() => {
    if (socket) {
      if (isLoading) {
        socket.emit('getArtTable', {
          token: accessToken,
          query: query,
        });
        setPagination(prev => ({
          ...prev,
          page: query.page ? query.page - 1 : 0,
        }));
      }
    }
  }, [accessToken, socket, isLoading, query]);
  const useFieldDefinitions = (socket, accessToken) => {
    const [loadingDefinitions, setLoadingDefinitions] = React.useState({});
    const [definitionDataMap, setDefinitionDataMap] = React.useState({});

    const loadFieldDefinition = useCallback(
      (fieldId, rowId) => {
        if (!loadingDefinitions[fieldId] && !definitionDataMap[fieldId]) {
          setLoadingDefinitions(prev => ({ ...prev, [fieldId]: true }));
          socket.emit('getArtFieldDefinition', {
            fieldId,
            rowId,
            token: accessToken,
          });
          socket.once('artFieldDefinition', data => {
            setDefinitionDataMap(prev => ({ ...prev, [fieldId]: data }));
            setLoadingDefinitions(prev => ({ ...prev, [fieldId]: false }));
          });
        }
      },
      [socket, accessToken, loadingDefinitions, definitionDataMap],
    );

    return { loadingDefinitions, definitionDataMap, loadFieldDefinition };
  };
  const { loadingDefinitions, definitionDataMap, loadFieldDefinition } =
    useFieldDefinitions(socket, accessToken);

  const cellRenderer = useCallback(
    ({ row, column }) => {
      let fieldData = row[column.fieldId];
      if (fieldData && typeof fieldData === 'object') {
        fieldData = fieldData.valueName || fieldData.valueId || '';
      } else if (
        fieldData &&
        (typeof fieldData === 'string' || typeof fieldData === 'number')
      ) {
        const values = definitionDataMap?.[column.fieldId]?.values || [];
        const found = values.find(
          opt => String(opt.valueId) === String(fieldData),
        );
        fieldData = found ? found.valueName : fieldData;
      }
      if (fieldData && column.fieldType === 'bool') {
        if (fieldData == '1') {
          fieldData = getLocalString('labels', 'yes');
        }
        if (fieldData == '0') {
          fieldData = getLocalString('labels', 'no');
        }
      }
      if (
        column.fieldType === 'monthly_distribution' ||
        column.fieldType === 'loading_percent' ||
        column.fieldType === 'cost_fact' ||
        column.fieldType === 'labor_fact' ||
        column.fieldType === 'date'
      ) {
        return (
          <Box align='left'>
            <Tooltip title={column.fieldName}>
              <Typography
                textOverflow='ellipsis'
                overflow='hidden'
                whiteSpace='nowrap'
                variant='h5'
                sx={{
                  color: row.columnsEditable[column.fieldId].isManual
                    ? 'red'
                    : 'inherit',
                }}
              >
                {fieldData}
              </Typography>
            </Tooltip>
          </Box>
        );
      } else if (column.fieldType === 'issueId') {
        return (
          <Box align='left'>
            <Tooltip title={column.fieldName}>
              <a
                href={handleGetRedirectUrl({
                  id: row.services.issueId,
                  columnType: 'issue',
                })}
              >
                <Typography
                  variant='h5'
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row[column.fieldId]}
                </Typography>
              </a>
            </Tooltip>
          </Box>
        );
      } else if (column.fieldType === 'issueId') {
        return (
          <Box
            align='left'
            sx={{
              overflow: 'hidden',
            }}
          >
            {row.services.isNew || row.kind === 'project' ? (
              ''
            ) : (
              <Tooltip title={column.fieldName}>
                <Typography
                  variant='h5'
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fieldData}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      } else {
        return (
          <Box
            align='left'
            sx={{
              overflow: 'hidden',
            }}
          >
            <Tooltip title={column.fieldName}>
              <Typography
                variant='h5'
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {fieldData}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
    },
    [getLocalString, definitionDataMap, handleGetRedirectUrl],
  );
  const handleField = useCallback(
    (fieldValue, props, column) => {
      setDisableSaveButton(true);
      let newValue =
        column.fieldType === 'date' ? fieldValue : fieldValue.target.value;
      let socketValue =
        column.fieldType === 'date' ? fieldValue : fieldValue.target.value;
      ['float', 'int'].includes(column.fieldType) &&
        (socketValue = socketValue.replace(/\s/g, '').replace(',', '.'));
      if (column.fieldType === 'bool') {
        if (fieldValue.target.value === 'true') {
          newValue = getLocalString('labels', 'yes');
        }
        if (fieldValue.target.value === 'false') {
          newValue = getLocalString('labels', 'no');
        }
      }
      if (column.fieldType === 'date') {
        if (fieldValue && fieldValue.$d) {
          newValue = dayjs(fieldValue.$d).format('DD.MM.YYYY');
          socketValue = dayjs(fieldValue.$d).format('YYYY-MM-DD');
        } else {
          newValue = null;
          socketValue = null;
        }
      }
      if (socket) {
        socket.emit('updateArtCell', {
          rowId: props.id,
          fieldId: props.field,
          valueId: socketValue,
          token: accessToken,
        });
      }
      props.api.setEditCellValue({
        id: props.id,
        field: props.field,
        value: newValue,
      });
    },
    [socket, getLocalString, accessToken],
  );
  const handleNumberField = useCallback(
    (value, props, column) => {
      const reg = new RegExp(/^[0-9\s,.-]*$/);
      if (reg.test(value.target.value)) {
        handleField(value, props, column);
      }
    },
    [handleField],
  );

  const renderEditCell = useCallback(
    (props, column) => {
      const { row, field, value, api, id } = props;
      const columnfind = props.row.columnsEditable[field];
      const fieldDef = definitionDataMap?.[field] || column;
      switch (column.fieldType) {
        case 'date':
          let date = props.value;
          if (typeof props.value === 'string') {
            const [day, month, year] = props.value.split('.');
            date = `${year}-${month}-${day}`;
          }
          return (
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={language}
            >
              <StyledDatePicker
                changedStyle={props.row.changed}
                format={'DD.MM.YYYY'}
                value={dayjs(date)}
                //renderInput={params => <TextField {...params} placeholder={getLocalString('labels', 'input')} />}
                //autoFocus={true}
                onChange={value => handleField(value, props, column)}
                slots={{
                  leftArrowIcon: () => (
                    <ChevronIcon
                      direction='left'
                      viewBox='0 0 24 24'
                      width={24}
                      height={24}
                    />
                  ),
                  rightArrowIcon: () => (
                    <ChevronIcon
                      direction='right'
                      viewBox='0 0 24 24'
                      width={24}
                      height={24}
                    />
                  ),
                  rightArrowButton: componentProps => (
                    <IconButton disableRipple {...componentProps}>
                      {componentProps.children}
                    </IconButton>
                  ),
                  leftArrowButton: componentProps => (
                    <IconButton disableRipple {...componentProps}>
                      {componentProps.children}
                    </IconButton>
                  ),
                }}
              />
            </LocalizationProvider>
          );

        case 'enumeration':
        case 'user':
        case 'list': {
          return (
            <ArtEditCellSelect
              field={field}
              rowId={row.rowId}
              accessToken={accessToken}
              socket={socket}
              cellProps={props}
              handleField={(val, cellProps) =>
                handleField(val, cellProps, column)
              }
              getOptionLabel={option =>
                typeof option === 'string'
                  ? option
                  : option?.label || option?.name || option?.valueName || ''
              }
            />
          );
        }
        case 'bool': {
          let radioValue;
          if (
            value === true ||
            value === 'true' ||
            value === 1 ||
            value === '1' ||
            value === getLocalString('labels', 'yes')
          ) {
            radioValue = 'true';
          } else if (
            value === false ||
            value === 'false' ||
            value === 0 ||
            value === '0' ||
            value === getLocalString('labels', 'no')
          ) {
            radioValue = 'false';
          } else {
            radioValue = '';
          }
          return (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  zIndex: 2,
                  background: 'white',
                  pointerEvents: 'auto',
                  p: 0,
                  m: 0,
                }}
              >
                <FormControl sx={{ m: 0, p: 0 }}>
                  <RadioGroup
                    value={radioValue}
                    onChange={value => handleField(value, props, column)}
                    row
                    sx={{
                      gap: 1,
                      p: 0,
                      m: 0,
                      '& .MuiFormControlLabel-root': { m: 0, p: 0 },
                    }}
                  >
                    <FormControlLabel
                      value='true'
                      control={
                        <Radio
                          icon={
                            <CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                          checkedIcon={
                            <CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                        />
                      }
                      label={getLocalString('labels', 'yes')}
                    />
                    <FormControlLabel
                      value='false'
                      control={
                        <Radio
                          icon={
                            <CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                          checkedIcon={
                            <CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                          }
                        />
                      }
                      label={getLocalString('labels', 'no')}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
          );
        }

        case 'attachment':
          return (
            <Button variant='contained' component='label'>
              {getLocalString('labels', 'choose_file')}
              <input
                type='file'
                hidden
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    handleChange(file.name);
                  }
                }}
              />
            </Button>
          );

        case 'float':
        case 'int':
          return (
            <StyledRowInput
              changedStyle={props.row.changed}
              value={value ?? ''}
              onChange={e => handleNumberField(e, props, column)}
              fullWidth
              //type="number"
              variant='standard'
              placeholder={getLocalString('labels', 'input')}
              //autoFocus
              slotProps={{
                input: {
                  //disableUnderline: true,
                  style: {
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                  },
                },

                htmlInput: {
                  style: {
                    textAlign: 'left',
                    appearance: 'textfield',
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                  },
                },
              }}
            />
          );

        case 'text':
        case 'string':
        default:
          return (
            <TextField
              value={value ?? ''}
              onChange={e => handleField(e, props, column)}
              fullWidth
              variant='standard'
              placeholder={''}
              autoFocus
              slotProps={{
                input: {
                  disableUnderline: true,
                  style: {
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                  },
                },

                htmlInput: {
                  style: { textAlign: 'left' },
                },
              }}
            />
          );
      }
    },
    [
      definitionDataMap,
      language,
      getLocalString,
      handleField,
      accessToken,
      socket,
      handleNumberField,
    ],
  );
  const handleClearArt = useCallback(() => {
    return new Promise(resolve => {
      setIsLoading(true);
      resolve();
    });
  }, []);
  const handleSaveArt = useCallback(() => {
    return new Promise(resolve => {
      setArtRowsErrors([]);
      if (socket) {
        socket.emit('checkArtSaveConflict', {
          token: accessToken,
        });
        socket.once('artEndRowsSave', () => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }, [accessToken, socket]);
  const formattedArtColumns = useCallback(
    columns => {
      const issueIdField = {
        instance: 'issue',
        field: 'id',
        fieldId: 'issue.issue_id',
        fieldName: 'Id',
        fieldType: 'issueId',
        isChecked: true,
        isCustom: false,
        position: 0,
        canSort: true,
      };
      let newColumns = [issueIdField].concat(columns);
      newColumns = newColumns.map(column => {
        let type = '';
        let editable = true;
        const numberFields = ['float', 'int', 'percent'];
        return {
          field: column.fieldId,
          headerName: column.fieldName,
          renderHeader: props => (
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {props.colDef.headerName}
            </Typography>
          ),
          renderCell: props => {
            const row = props.row;
            return cellRenderer({ row, column });
          },
          disableExport: false,
          type: type,
          position: column.position,
          sortable: column.canSort,
          editable: editable,
          renderEditCell: props => renderEditCell(props, column),
          cellClassName: 'tableCell',
        };
      });
      return newColumns.toSpliced(1, 0, {
        field: GRID_TREE_DATA_GROUPING_FIELD,
      });
    },
    [cellRenderer, renderEditCell],
  );
  const formattedArtRow = useCallback(
    row => {
      if (row.rowType === 'normal') {
        const newCols = row.columns.reduce((columnAcc, _, i) => {
          return {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].value,
          };
        }, {});
        const columnsEditable = row.columns.reduce((columnAcc, _, i) => {
          return {
            ...columnAcc,
            [row.columns[i].fieldId]: {
              isEditable: row.columns[i].isEditable,
              isManual: row.columns[i].isManual,
              needRequestDefinition: row.columns[i].needRequestDefinition,
            },
          };
        }, {});
        return [
          {
            id: row.rowId,
            kind: row.type,
            project: Number(row.services.projectId),
            ['issue.issue_id']: '#' + row.rowId,
            ...newCols,
            columnsEditable: columnsEditable,
            services: row.services,
            editable: row.isEditable,
            rowName: row.rowName,
            rowId: row.rowId,
            type: row.type,
            signatures: row.signatures,
          },
        ];
      }
      return [];
    },

    [],
  );

  const formattedArtRows = useCallback(
    rows =>
      rows.reduce((acc, issue) => {
        if (issue.rowType === 'normal') {
          const newCols = issue.columns.reduce((columnAcc, _, i) => {
            return {
              ...columnAcc,
              [issue.columns[i].fieldId]: issue.columns[i].value,
            };
          }, {});
          const columnsEditable = issue.columns.reduce((columnAcc, _, i) => {
            return {
              ...columnAcc,
              [issue.columns[i].fieldId]: {
                isEditable: issue.columns[i].isEditable,
                isManual: issue.columns[i].isManual,
                needRequestDefinition: issue.columns[i].needRequestDefinition,
              },
            };
          }, {});
          return [
            ...acc,
            {
              id: issue.rowId,
              kind: issue.type,
              project: Number(issue.services.projectId),
              ['issue.issue_id']: '#' + issue.rowId,
              ...newCols,
              columnsEditable: columnsEditable,
              services: issue.services,
              editable: issue.isEditable,
              rowName: issue.rowName,
              rowId: issue.rowId,
              type: issue.type,
              signatures: issue.signatures,
            },
          ];
        }
        return [];
      }, []),

    [],
  );
  useEffect(() => {
    if (socket) {
      let tempData = {};
      let tempArtData = {};
      socket.on('artFieldsTable', data => {
        tempArtData.artColumns = data;
        setArtTableColumnsData(formattedArtColumns(tempArtData.artColumns));
      });
      socket.on('artRowsTable', data => {
        tempArtData.artData = data;
        const total = parseInt(data.paging?.total) || 0;
        const limit = parseInt(data.paging?.limit) || 100;
        const pageCount = Math.ceil(total / limit);
        setPagination(prev => ({
          ...prev,
          count: pageCount,
          page: Math.floor(parseInt(data.paging?.offset || 0) / limit),
          limit: limit,
        }));

        setArtTableData(data);
        setArtTableRowsData(formattedArtRows(tempArtData.artData.rows));
      });
      socket.on('artEndGetTable', data => {
        setIsLoading(false);
      });
      socket.on('artRefreshRowsTable', data => {
        setDisableSaveButton(false);
        let updatedRowData = formattedArtRow(data);
        apiRef.current.updateRows(updatedRowData);
        setDisableSaveButton(false);
      });
      socket.on('artSomeConflictRows', data => {
        setArtRowsErrors(data);
      });
      socket.on('artNoConflictRows', data => {
        socket.emit('saveArtRows', {
          token: accessToken,
        });
      });
      socket.on('artRowsSaved', data => {
        setIsLoading(true);
      });
    }
    return () => {
      if (socket) {
        socket.off('connect');
        socket.off('artEndGetTable');
        socket.off('getArtTable');
        socket.off('artRowsTable');
        socket.off('artFieldsTable');
        socket.off('artRowsNotSaved');
        socket.off('artStartUpdateCell');
        socket.off('artEndUpdateCell');
        socket.off('artRefreshRowsTable');
        socket.off('rowNotFound');
        socket.off('cannotEditRow');
        socket.off('noRowsForUpdate');
        socket.off('artSomeConflictRows');
        socket.off('artNoConflictRows');
        socket.off('artEndConflictCheck');
        socket.off('artStartConflictCheck');
        socket.off('artStartRowsSave');
        socket.off('artEndRowsSave');
        socket.off('artRowsSaved');
      }
    };
  }, [
    formattedArtColumns,
    formattedArtRow,
    formattedArtRows,
    rowsToUpdate,
    tempCellValue,
    socket,
    apiRef,
    setArtRowsErrors,
    handleError,
    accessToken,
  ]);

  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);

  // useEffect(() => {
  //   if (artTableData && artTableData.rows?.length > 0) {
  //     artTableData.paging.total !== undefined &&
  //       setPagination(prev => ({ ...prev, count: Number(artTableData.paging.total) }));
  //   }
  // }, [artTableData]);
  const changePagination = useCallback(
    newPage => {
      setQuery(prev => ({
        ...prev,
        page: newPage,
        offset: (newPage - 1) * 100,
      }));
      setIsLoading(true);
    },
    [setQuery, setIsLoading],
  );
  const handlePageChangeWithWarning = useCallback(
    (event, newPage) => {
      if (hasUnsavedChanges) {
        setShowWarningModal(true);
        setPendingPageChange(newPage);
      } else {
        changePagination(newPage);
      }
    },
    [hasUnsavedChanges, changePagination],
  );
  const handleSaveAndProceed = useCallback(async () => {
    setShowWarningModal(false);
    try {
      await handleSaveArt();
      setDisableSaveButton(true);
      if (pendingPageChange) {
        changePagination(pendingPageChange);
        setPendingPageChange(null);
      }
    } catch (error) {
      console.error('Error during save:', error);
    }
  }, [handleSaveArt, pendingPageChange, changePagination]);

  const handleDiscardAndProceed = useCallback(async () => {
    setShowWarningModal(false);
    setDisableSaveButton(true);
    try {
      await handleClearArt();
      if (pendingPageChange) {
        changePagination(pendingPageChange);
        setPendingPageChange(null);
      }
    } catch (error) {
      console.error('Error during discard:', error);
    }
  }, [handleClearArt, pendingPageChange, changePagination]);
  function Pagination({ page, onPageChange, className }) {
    return (
      <MuiPagination
        color='primary'
        className={className}
        count={pagination.count}
        siblingCount={1}
        boundaryCount={0}
        page={pagination.page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  ></PaginationItem>
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={handlePageChangeWithWarning}
      />
    );
  }
  useEffect(() => {
    setHasUnsavedChanges(!disableSaveButton);
  }, [disableSaveButton]);

  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);
  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);

  const handleSelectFilters = useCallback(
    data => {
      if (!data.hasOwnProperty('operation')) {
        return;
      }
      const items = currentParams.filters.selectedItems.map(item => {
        if (item.fieldId === data.fieldId) {
          return data;
        } else {
          return item;
        }
      });
      const updatedParams = {
        ...currentParams,
        filters: { ...currentParams.filters, selectedItems: items },
      };
      const paramsToQueryFormated = paramsToQuery(updatedParams);
      setCurrentParams(updatedParams);
      setQuery(paramsToQueryFormated);
      if (paramsToQueryFormated.filters != query.filters) {
        setIsLoading(true);
      }
    },
    [currentParams, setQuery, query],
  );
  function CustomPagination(props) {
    return (
      <GridPagination
        {...props}
        labelDisplayedRows={() => ''}
        rowsPerPageOptions={[]}
        ActionsComponent={Pagination}
      />
    );
  }

  const handleSaveAfterError = useCallback(() => {
    if (artErrorValue == 'cancel_changes') {
      setArtRowsErrors([]);
      setIsLoading(true);
      setDisableSaveButton(true);
    } else {
      if (socket) {
        setArtRowsErrors([]);
        socket.emit('saveArtRows', {
          token: accessToken,
        });
        setDisableSaveButton(true);
      }
    }
  }, [accessToken, socket, artErrorValue]);
  const downloadCsv = useCallback(e => {
    async function downloadGanttCsv() {
      try {
        setDownloadLoading(true);
        const params = { project: 696 };
        if (params) {
          const { data } = await getGantCsv(params);
          const url = window.URL.createObjectURL(
            new Blob(['\ufeff', data], { type: 'text/csv;charset=utf-8;' }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `gantt-${dayjs().format('YYYY-MM-DD')}`,
          );
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          link.remove();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDownloadLoading(false);
      }
    }
    downloadGanttCsv();
  }, []);
  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate('/finances');
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate],
  );

  useEffect(() => {
    if (ganttStartDateErrors) {
      if (tempCellValue) {
        apiRef.current.updateRows([tempCellValue]);
      }
    }
  }, [tempCellValue, ganttStartDateErrors, apiRef]);
  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.SAVE:
          handleSaveArt();
          break;
        case ButtonsTypes.CANCEL:
          handleClearArt();
          break;
        case ButtonsTypes.QUERY:
          setQueryModalState('create');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.EDIT_QUERY:
          setQueryModalState('edit');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.DELETE_QUERY:
          setOpenWarningModal(true);
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
      }
    },
    [handleSaveArt, handleClearArt, setOpenParamsModal],
  );
  const closeWarningModal = useCallback(() => {
    setShowWarningModal(false);
    setPendingPageChange(null);
  }, []);
  return (
    <>
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      <WarningModal
        simple
        isWarningModalOpen={showWarningModal}
        closeWarningModal={closeWarningModal}
        handleToggle={e =>
          e.currentTarget.attributes.value.value === 'save'
            ? handleSaveAndProceed()
            : handleDiscardAndProceed()
        }
        title={getLocalString('ArtContainer', 'messages', 'unsavedChanges')}
      />
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      <GridSaveQueryModal
        state={queryModalState}
        saveLoader={saveLoader}
        permission={'canSaveIssuesGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
      />
      <CommonPage
        pageTitle={pageTitle}
        loading={isLoading}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        disableSave={disableSaveButton}
        component={'ArtContainer'}
      >
        {currentParams && currentParams.filters?.selectedItems?.length > 0 && (
          <Box sx={{ marginTop: '10px' }}>
            <GridFiltersPanelNew
              data={currentParams.filters.selectedItems}
              handleCloseFilter={handleSelectFilters}
            ></GridFiltersPanelNew>
          </Box>
        )}
        {artRowsErrors.length > 0 && (
          <Grid
            container
            direction='column'
            sx={{
              marginBottom: 2,
            }}
          >
            <Typography
              variant='h3'
              sx={{
                fontWeight: 600,
                color: '#E03737',
              }}
            >
              {getLocalString('errors', 'issueUpdated')}
            </Typography>
            {artRowsErrors.map((error, index) => (
              <Box key={index}>
                <Typography
                  variant='h3'
                  sx={{
                    color: '#E03737',
                  }}
                >
                  {error.name}
                </Typography>
              </Box>
            ))}
            <Grid
              container
              wrap='nowrap'
              direction='column'
              spacing={0}
              sx={{
                marginLeft: 4,
              }}
            >
              <RadioGroup
                value={artErrorValue}
                onChange={e => setArtErrorValue(e.target.value)}
                defaultValue={'save_all'}
              >
                <FormControlLabel
                  value='save_all'
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString(
                    'ArtContainer',
                    'messages',
                    'savingArt',
                  )}
                />
                <FormControlLabel
                  value='cancel_changes'
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <UncheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                      checkedIcon={
                        <CheckedRadioIcon
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                        />
                      }
                    />
                  }
                  label={getLocalString('messages', 'clearMyChanges')}
                />
              </RadioGroup>
              <Grid>
                <Button
                  variant='defaultBluePrimary'
                  disableRipple
                  onClick={handleSaveAfterError}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {getLocalString('labels', 'accept')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
        {isLoading ? (
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <CircularProgress color='secondary' />
          </Grid>
        ) : (
          <>
            <Grid
              container
              sx={{
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '16px',
                marginTop: '20px',
              }}
            >
              <StyledDataGridTableRoot
                localeText={{
                  noRowsLabel: getLocalString('labels', 'noData'),
                }}
                apiRef={apiRef}
                pagination
                sx={{
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                  [`& .MuiDataGrid-columnHeader`]: {
                    maxHeight: 'none !important',
                    minHeight: '75px !important',
                    height: 'auto !important',
                    whiteSpace: 'inherit !important',
                    overflow: 'inherit !important',
                    lineHeight: '24px !important',
                  },
                  [`& .MuiDataGrid-columnHeaderTitle`]: {
                    whiteSpace: 'normal !important',
                    minHeight: '75px !important',
                  },
                  [`& .MuiDataGrid-columnHeaderTitleContainer`]: {
                    whiteSpace: 'normal !important',
                    minHeight: '75px !important',
                  },
                }}
                columnHeaderHeight={75}
                isCellEditable={isCellEditable}
                disableColumnFilter
                disableRowSelectionOnClick
                disableColumnMenu
                columns={artTableColumnsData}
                rows={artTableRowsData}
                getRowId={item => item.id}
                sortingMode='server'
                sortModel={sortModel}
                onSortModelChange={handleSort}
                slotProps={{
                  baseCheckbox: {
                    icon: (
                      <CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ),
                    checkedIcon: (
                      <CheckboxIconChecked
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ),
                  },
                }}
                slots={{
                  pagination: CustomPagination,
                  columnUnsortedIcon: () => (
                    <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                  ),
                  columnSortedAscendingIcon: () => (
                    <SortIcon
                      direction='up'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                  columnSortedDescendingIcon: () => (
                    <SortIcon
                      direction='down'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                }}
                getRowClassName={params => params.row.groupName && 'groupRow'}
                isRowSelectable={params => !params.row.groupName}
                getRowHeight={() => 'auto'}
              />
            </Grid>
          </>
        )}
      </CommonPage>
    </>
  );
};

export default ArtContainer;
